import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import dayjs, { Dayjs } from "dayjs";
import { parseWallClockTime } from '../../../shared/Helpers/timezones';

import { CustomButton, Text } from "../../../shared/uiComponents";
import ClockInDialog from "../../../components/ClockInDialog";
import { SessionType as SessionTypeEnum } from "../../../shared/Helpers/constants";
import { Loader } from "../../../components/StyledComponents";
import { Calendar } from "../../../shared/Assets/menuIcons";
import {
  DashboardClockSvg,
  TelehealthSvg,
  LocationPinSvg,
  PeopleGroupSvg,
  CalendarOutlineSvg,
} from "../../../shared/Assets/svgIcons";
import SummaryCards from "./SummaryCards";
import IncompleteNotesWidget from "./IncompleteNotesWidget";
import MyClientsWidget from "./MyClientsWidget";
import { errorNormalizer, getClientDisplayLabel } from "../../../shared/Helpers/functions";
import { PRIMARY } from "../../../shared/Helpers/colors";
import { getSessionStatus, getSessionButtonProps } from "../helpers";

import { DispatchProperties, useSelector } from "../../../redux/store";
import {
  getBtDashboard,
  BtDashboardSession,
} from "../../../redux/State/clientSlice/dashboardSlice";
import { startEventSession, startSession } from "../../../redux/State/clientSlice/sessionSlice";
import {
  buildTodayGhostSessions,
  mergeTodaySessions,
} from "./recurringScheduleDerivation";

const formatTime = (isoString: string) => parseWallClockTime(isoString).format('h:mm A');

const meetAndGreetTypes = [SessionTypeEnum.MeetAndGreet, SessionTypeEnum.MeetAndGreetNonBillable];

export const BtSessionCard = ({ session }: { session: BtDashboardSession }) => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clockInDialogOpen, setClockInDialogOpen] = useState(false);

  const canSkipSession = meetAndGreetTypes.includes(session.sessionTypeType);

  const handleStartSession = () => {
    if (session.sessionId) {
      navigate(`/calendar/session/${session.sessionId}/${session.client.id}`);
      return;
    }

    if (session.isRecurringScheduleOccurrence) {
      setClockInDialogOpen(true);
      return;
    }

    executeStartSession();
  };

  const executeStartSession = (clockInStart?: Dayjs | null, clockInEnd?: Dayjs | null) => {
    setLoading(true);
    // A recurring-schedule occurrence has no real event yet: starting it creates the real session
    // from the scheduled window (provider resolved server-side from the token), mirroring the
    // calendar's recurring start flow. Existing real events still start via the event id.
    const dateStr = session.startTime.slice(0, 10);
    const request = session.isRecurringScheduleOccurrence
      ? dispatch(
          startSession({
            clientId: session.client.id,
            date: dateStr,
            startTime: clockInStart ? `${dateStr}T${clockInStart.format('HH:mm')}` : session.startTime,
            endTime: clockInEnd ? `${dateStr}T${clockInEnd.format('HH:mm')}` : session.endTime,
            sessionTypeId: session.sessionTypeId,
            recurringScheduleId: session.recurringScheduleId,
            recurringScheduleTimeSlotId: session.recurringScheduleTimeSlotId,
          })
        ).then(unwrapResult)
      : dispatch(startEventSession(session.eventId)).then(unwrapResult);

    request
      .then((response) => {
        if (!response.id || !response.client?.id) return;
        setClockInDialogOpen(false);
        navigate(`/calendar/session/${response.id}/${response.client.id}`);
      })
      .catch(errorNormalizer)
      .finally(() => setLoading(false));
  };

  const handleViewClient = () => {
    navigate(`/clients/client/${session.client.id}`);
  };

  const clientLabel = getClientDisplayLabel(session.client);

  const locationDisplay = session.isTelehealth
    ? null
    : session.locationAddress || session.locationName || (session.client.isElc ? 'ELC' : null);

  return (
    <div
      className="btSessionCard"
      style={undefined}
    >
      <div className="btSessionCardHeader">
        <div className="btSessionCardTime">
          <span className="btSessionCardTimeIcon">
            <DashboardClockSvg />
          </span>
          <span className="btSessionCardTimeText">{`${formatTime(session.startTime)} - ${formatTime(session.endTime)}`}</span>
        </div>
        <span className={`btStatusBadge ${getSessionStatus(session).className}`}>
          {getSessionStatus(session).label}
        </span>
      </div>
      <div className="btSessionCardBody">
        <Text
          className="btSessionClientName"
          title={clientLabel}
          type="h6"
          font="header"
          fontWeight="semiBold"
        />
        <div className="btSessionCardMeta">
          {session.isTelehealth ? (
            <span className="btSessionCardMetaItem">
              <TelehealthSvg />
              Telehealth
            </span>
          ) : locationDisplay ? (
            <span className="btSessionCardMetaItem">
              <LocationPinSvg />
              {locationDisplay}
            </span>
          ) : null}
          {session.activeTargetsCount > 0 && (
            <span className="btSessionCardMetaItem">
              <PeopleGroupSvg />
              {session.activeTargetsCount} active target{session.activeTargetsCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>
      <div className="btSessionCardAction">
        <CustomButton
          title={getSessionButtonProps({ isCompleted: session.isCompleted, sessionId: session.sessionId, canSkipSession }).title}
          sizeType="sm"
          onClick={handleStartSession}
          loading={loading}
          disabled={getSessionButtonProps({ isCompleted: session.isCompleted, sessionId: session.sessionId, canSkipSession }).disabled}
          style={{ background: PRIMARY.BASE, border: `1px solid ${PRIMARY.BASE}` }}
        />
        <CustomButton
          title="View Client"
          sizeType="sm"
          buttonType="outlined"
          onClick={handleViewClient}
          style={{ borderColor: PRIMARY.BASE, color: PRIMARY.BASE, background: "transparent" }}
        />
      </div>
      {session.isRecurringScheduleOccurrence && (
        <ClockInDialog
          open={clockInDialogOpen}
          onClose={() => setClockInDialogOpen(false)}
          onConfirm={(start, end) => executeStartSession(start, end)}
          loading={loading}
          scheduledStartTime={session.startTime}
          scheduledEndTime={session.endTime}
        />
      )}
    </div>
  );
};

const BtDashboard = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();

  const loading = useSelector((state) => state.dashboard.loading);
  const btDashboard = useSelector((state) => state.dashboard.btDashboard);
  const error = useSelector((state) => state.dashboard.error);
  useEffect(() => {
    dispatch(getBtDashboard());
  }, [dispatch]);

  if (loading) return <Loader />;

  if (error?.status && !btDashboard) {
    return (
      <div className="btEmptyState">
        <Text
          title="Unable to load dashboard"
          type="h6"
          font="header"
          fontWeight="semiBold"
        />
        <Text
          title={error.title || "Something went wrong. Please try again later."}
          type="psm"
        />
        <CustomButton
          title="Retry"
          sizeType="sm"
          onClick={() => dispatch(getBtDashboard())}
          style={{ background: PRIMARY.BASE, border: `1px solid ${PRIMARY.BASE}` }}
        />
      </div>
    );
  }

  const realSessions = btDashboard?.sessions ?? [];
  const recurringSchedules = btDashboard?.recurringSchedules ?? [];
  const incompleteNotes = btDashboard?.incompleteNotes ?? { count: 0, draftCount: 0, notStartedCount: 0, soonestExpiryHours: null };
  const baseClients = btDashboard?.clients ?? [];

  const ghostSessions = buildTodayGhostSessions(recurringSchedules, realSessions, baseClients, dayjs(), btDashboard?.cancellations ?? []);
  const sessions = mergeTodaySessions(realSessions, ghostSessions);

  return (
    <div className="btDashboardContainer">
      <SummaryCards summary={btDashboard?.summary ?? null} />
      <IncompleteNotesWidget summary={incompleteNotes} />
      <div className="btSectionHeader">
        <div className="btSectionHeaderLeft">
          <Calendar color="#313130" />
          <Text
            className="btSectionTitle"
            title={`Scheduled Sessions`}
            type="h6"
            font="header"
            fontWeight="semiBold"
          />
          <span className="btSectionCount">{sessions.length} session{sessions.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="btSectionHeaderRight">
          <CustomButton
            className="btHeaderLink"
            title="View Full Calendar"
            buttonType="link"
            sizeType="sm"
            startIcon={<CalendarOutlineSvg />}
            onClick={() => navigate("/calendar")}
          />
        </div>
      </div>
      {sessions.length === 0 ? (
        <div className="btEmptyState">
          <Text
            title="No sessions scheduled for today"
            type="h6"
            font="header"
            fontWeight="semiBold"
          />
          <Text
            title="Enjoy your day off, or check back later for updates."
            type="psm"
          />
        </div>
      ) : (
        <div className="btSessionsList">
          {sessions.map((session) => (
            <BtSessionCard key={session.eventId || `${session.recurringScheduleId}-${session.recurringScheduleTimeSlotId ?? session.startTime}`} session={session} />
          ))}
        </div>
      )}
      <MyClientsWidget clients={baseClients} />
    </div>
  );
};

export default BtDashboard;
